import { Controller } from "@hotwired/stimulus"

// Inline Google Places autocomplete for the Imagine address step.
//
// This used to run inside an iframe (imagine/address_fields#show) so the widget lived in
// its own short document — on the tall, document-scrolled chat page, focusing an inline
// field makes iOS Safari pan the visual viewport and shove the field off the top. We now
// mount the PlaceAutocompleteElement directly into this element, mirror the picked address
// into the hidden field the form submits (firing `input` so imagine-control re-enables
// Continue), and defeat the iOS pan by scrolling the page back to the top once the
// keyboard settles (see the focusin handler).
export default class extends Controller {
  static targets = ["mount", "fallback"]
  static values = {
    apiKey: String,
    placeholder: String,
    lat: Number,
    lng: Number,
    radius: Number
  }

  async connect() {
    if (!this.apiKeyValue) return

    try {
      this.#bootstrapGoogleMaps()
      const { PlaceAutocompleteElement } = await google.maps.importLibrary("places")

      const widget = new PlaceAutocompleteElement({ includedRegionCodes: ["us"] })

      // Bias toward the account's service area (US-only is still enforced above).
      if (this.hasLatValue && this.hasLngValue && this.hasRadiusValue) {
        widget.locationBias = {
          center: { lat: this.latValue, lng: this.lngValue },
          radius: this.radiusValue
        }
      }
      if (this.placeholderValue) widget.setAttribute("placeholder", this.placeholderValue)
      widget.setAttribute("no-input-icon", "")
      Object.assign(widget.style, {
        colorScheme: "light",
        backgroundColor: "#ffffff",
        color: "#2c2c2c",
        border: "1px solid rgba(0,0,0,0.2)",
        borderRadius: "0.375rem",
        fontFamily: "'Montserrat', system-ui, sans-serif",
        fontSize: "1rem",
        display: "block",
        width: "100%"
      })

      widget.addEventListener("gmp-select", async (event) => {
        const place = event.placePrediction.toPlace()
        await place.fetchFields({ fields: ["formattedAddress"] })
        this.fallbackTarget.value = place.formattedAddress
        this.fallbackTarget.dispatchEvent(new Event("input", { bubbles: true }))
      })

      // iOS Safari pans a freshly-focused field off the top of this tall, document-scrolled
      // page. Doing it by hand — finger-scrolling the page to the very top once the keyboard
      // is up — fixes it, so mimic exactly that: scroll the window to y=0. Safari ignores
      // scrolls fired synchronously during the focus + keyboard animation, so re-assert once
      // the visual viewport has resized (keyboard up), plus a delayed backstop in case that
      // event doesn't land. (focusin bubbles out of the widget's shadow DOM; focus would not.)
      widget.addEventListener("focusin", () => {
        if (window.visualViewport) {
          window.visualViewport.addEventListener("resize", this.scrollPageToTop, { once: true })
        }
        setTimeout(this.scrollPageToTop, 350)
      })

      this.mountTarget.appendChild(widget)
    } catch (e) {
      // API blocked/unavailable — the hidden field stays empty (Continue stays disabled).
    }
  }

  disconnect() {
    // Drop any resize listener still pending from a focus that never resized.
    window.visualViewport?.removeEventListener("resize", this.scrollPageToTop)
  }

  scrollPageToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" })
  }

  // Google Maps Dynamic Library Import bootstrap (queues importLibrary until loaded).
  // Idempotent: bails if the loader was already installed on this page.
  #bootstrapGoogleMaps() {
    if (window.google?.maps?.importLibrary) return

    ;(g => { var h, a, k, p = "The Google Maps JavaScript API", c = "google", l = "importLibrary", q = "__ib__", m = document, b = window; b = b[c] || (b[c] = {}); var d = b.maps || (b.maps = {}), r = new Set, e = new URLSearchParams, u = () => h || (h = new Promise(async (f, n) => { await (a = m.createElement("script")); e.set("libraries", [...r] + ""); for (k in g) e.set(k.replace(/[A-Z]/g, t => "_" + t[0].toLowerCase()), g[k]); e.set("callback", c + ".maps." + q); a.src = `https://maps.${c}apis.com/maps/api/js?` + e; d[q] = f; a.onerror = () => h = n(Error(p + " could not load.")); a.nonce = m.querySelector("script[nonce]")?.nonce || ""; m.head.append(a) })); d[l] ? 0 : d[l] = (f, ...n) => r.add(f) && u().then(() => d[l](f, ...n)) })({ key: this.apiKeyValue, v: "weekly" })
  }
}
